import axios from 'axios';

import {
  parseApproveDpiaForBE,
  parseDpiaDetailResponseForFE,
  parseMeasureForBE,
  parseMeasureForFE,
  parseRequestDpiaClarificationForBE,
  parseRiskForBE,
  parseRiskForFE,
  parseSubmitDpiaForBE,
  parseUpdateDpiaForBE,
  parseUpdatePurposeScopeForBE,
} from './dpias.parsers';
import type {
  ApproveDpiaInput,
  CreateDpiaMeasureInput,
  CreateDpiaRiskInput,
  DpiaDetailResponseDTO,
  DpiaMeasureDTO,
  DpiaRiskDTO,
  DpiasApi,
  RequestDpiaClarificationInput,
  UpdateDpiaMeasureInput,
  UpdateDpiaPurposeScopeInput,
  UpdateDpiaRiskInput,
} from './dpias.types';

const buildDetailEndpoint = (organizationId: number, dpiaId: number) => `api/v1/organizations/${organizationId}/dpias/${dpiaId}/`;

const buildReviewEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}review/`;

const buildSubmitEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}submit/`;

const buildApproveEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}approve/`;

const buildRequestClarificationEndpoint = (organizationId: number, dpiaId: number) =>
  `${buildDetailEndpoint(organizationId, dpiaId)}request-clarification/`;

const buildPurposeScopeEndpoint = (organizationId: number, dpiaId: number) =>
  `${buildDetailEndpoint(organizationId, dpiaId)}purpose-scope/`;

const buildRisksEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}risks/`;

const buildRiskEndpoint = (organizationId: number, dpiaId: number, riskId: number) =>
  `${buildRisksEndpoint(organizationId, dpiaId)}${riskId}/`;

const buildMeasuresEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}measures/`;

const buildMeasureEndpoint = (organizationId: number, dpiaId: number, measureId: number) =>
  `${buildMeasuresEndpoint(organizationId, dpiaId)}${measureId}/`;

export const dpiasApi: DpiasApi = {
  getDetail: async (organizationId, dpiaId) => {
    const { data } = await axios.get<DpiaDetailResponseDTO>(buildDetailEndpoint(organizationId, dpiaId));
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  getReviewDetail: async (organizationId, dpiaId) => {
    const { data } = await axios.get<DpiaDetailResponseDTO>(buildReviewEndpoint(organizationId, dpiaId));
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  update: async (organizationId, dpiaId, payload) => {
    const { data } = await axios.patch<DpiaDetailResponseDTO>(buildDetailEndpoint(organizationId, dpiaId), parseUpdateDpiaForBE(payload));
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  updatePurposeScope: async (organizationId, dpiaId, payload: UpdateDpiaPurposeScopeInput) => {
    const { data } = await axios.put<DpiaDetailResponseDTO>(
      buildPurposeScopeEndpoint(organizationId, dpiaId),
      parseUpdatePurposeScopeForBE(payload),
    );
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  createRisk: async (organizationId, dpiaId, payload: CreateDpiaRiskInput) => {
    const { data } = await axios.post<DpiaRiskDTO>(buildRisksEndpoint(organizationId, dpiaId), parseRiskForBE(payload));
    return { data: parseRiskForFE(data) };
  },
  updateRisk: async (organizationId, dpiaId, riskId, payload: UpdateDpiaRiskInput) => {
    const { data } = await axios.patch<DpiaRiskDTO>(buildRiskEndpoint(organizationId, dpiaId, riskId), parseRiskForBE(payload));
    return { data: parseRiskForFE(data) };
  },
  deleteRisk: async (organizationId, dpiaId, riskId) => {
    await axios.delete(buildRiskEndpoint(organizationId, dpiaId, riskId));
  },
  createMeasure: async (organizationId, dpiaId, payload: CreateDpiaMeasureInput) => {
    const { data } = await axios.post<DpiaMeasureDTO>(buildMeasuresEndpoint(organizationId, dpiaId), parseMeasureForBE(payload));
    return { data: parseMeasureForFE(data) };
  },
  updateMeasure: async (organizationId, dpiaId, measureId, payload: UpdateDpiaMeasureInput) => {
    const { data } = await axios.patch<DpiaMeasureDTO>(buildMeasureEndpoint(organizationId, dpiaId, measureId), parseMeasureForBE(payload));
    return { data: parseMeasureForFE(data) };
  },
  deleteMeasure: async (organizationId, dpiaId, measureId) => {
    await axios.delete(buildMeasureEndpoint(organizationId, dpiaId, measureId));
  },
  submit: async (organizationId, dpiaId, payload) => {
    const { data } = await axios.post<DpiaDetailResponseDTO>(
      buildSubmitEndpoint(organizationId, dpiaId),
      parseSubmitDpiaForBE(payload.revision),
    );
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  approve: async (organizationId, dpiaId, payload: ApproveDpiaInput) => {
    const { data } = await axios.post<DpiaDetailResponseDTO>(buildApproveEndpoint(organizationId, dpiaId), parseApproveDpiaForBE(payload));
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  requestClarification: async (organizationId, dpiaId, payload: RequestDpiaClarificationInput) => {
    const { data } = await axios.post<DpiaDetailResponseDTO>(
      buildRequestClarificationEndpoint(organizationId, dpiaId),
      parseRequestDpiaClarificationForBE(payload),
    );
    return { data: parseDpiaDetailResponseForFE(data) };
  },
};

export default dpiasApi;
